// src/components/events/MilestoneProgress.jsx
import React, { useState, useEffect } from 'react';
import { CheckCircle, Circle, Clock, Calendar } from 'lucide-react';
import { eventsService } from '../../services/eventsService';

export const MilestoneProgress = ({ bookId }) => {
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMilestones();
  }, [bookId]);

  const loadMilestones = async () => {
    try {
      const milestonesData = await eventsService.getEventMilestones(bookId);
      setMilestones(milestonesData);
    } catch (error) {
      console.error('Error loading milestones:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  const completedMilestones = milestones.filter(m => m.completed).length;
  const progressPercentage = milestones.length > 0 ? (completedMilestones / milestones.length) * 100 : 0;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="h-8 w-8 text-emerald-600" />
        </div>
        <h2 className="text-3xl font-bold mb-4">Event Planning Progress</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Track our progress as we prepare for this amazing event. 
          We'll keep you updated on key milestones and preparations.
        </p>
      </div>

      {/* Progress Overview */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Overall Progress</h3>
          <span className="text-2xl font-bold text-emerald-600">
            {Math.round(progressPercentage)}%
          </span>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
          <div 
            className="bg-emerald-600 h-3 rounded-full transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-emerald-600">{completedMilestones}</p>
            <p className="text-sm text-gray-600">Completed</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-yellow-600">
              {milestones.filter(m => !m.completed && new Date(m.targetDate) > new Date()).length}
            </p>
            <p className="text-sm text-gray-600">Upcoming</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-600">{milestones.length}</p>
            <p className="text-sm text-gray-600">Total</p>
          </div>
        </div>
      </div>

      {/* Milestones Timeline */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-6">Planning Timeline</h3>
        
        {milestones.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No milestones have been set yet.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {milestones.map((milestone, index) => {
              const isCompleted = milestone.completed;
              const isOverdue = !isCompleted && new Date(milestone.targetDate) < new Date();
              const daysUntil = Math.ceil((new Date(milestone.targetDate) - new Date()) / (1000 * 60 * 60 * 24));
              
              return (
                <div key={milestone.id} className="flex items-start gap-4">
                  {/* Status Icon */}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-1 ${
                    isCompleted ? 'bg-green-100' :
                    isOverdue ? 'bg-red-100' : 'bg-gray-100'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <Circle className={`h-5 w-5 ${isOverdue ? 'text-red-600' : 'text-gray-400'}`} />
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className={`font-medium ${isCompleted ? 'text-green-900 line-through' : 'text-gray-900'}`}>
                          {milestone.title}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {milestone.description}
                        </p>
                      </div>
                      
                      {/* Date & Status */}
                      <div className="text-right flex-shrink-0 ml-4">
                        <p className="text-sm font-medium">
                          {new Date(milestone.targetDate).toLocaleDateString()}
                        </p>
                        {isCompleted ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-700 mt-1">
                            Completed
                          </span>
                        ) : isOverdue ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-700 mt-1">
                            Overdue
                          </span>
                        ) : daysUntil <= 7 ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-700 mt-1">
                            Due {daysUntil === 0 ? 'today' : `in ${daysUntil} days`}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600 mt-1">
                            {daysUntil} days left
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Assignee */}
                    {milestone.assignedTo && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500">
                          Assigned to: {milestone.assignedTo}
                        </p>
                      </div>
                    )}
                    
                    {/* Completion Note */}
                    {milestone.completed && milestone.completionNote && (
                      <div className="mt-3 p-3 bg-green-50 rounded-lg border-l-4 border-green-400">
                        <p className="text-sm text-green-700">
                          <strong>Completed:</strong> {milestone.completionNote}
                        </p>
                        {milestone.completedAt && (
                          <p className="text-xs text-green-600 mt-1">
                            {new Date(milestone.completedAt.toDate()).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Updates */}
      {milestones.some(m => m.completed) && (
        <div className="bg-white rounded-xl shadow-sm p-6 mt-8">
          <h3 className="text-lg font-semibold mb-4">Recent Updates</h3>
          <div className="space-y-3">
            {milestones
              .filter(m => m.completed)
              .sort((a, b) => new Date(b.completedAt?.toDate() || 0) - new Date(a.completedAt?.toDate() || 0))
              .slice(0, 3)
              .map(milestone => (
                <div key={milestone.id} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-green-900">{milestone.title}</p>
                    <p className="text-sm text-green-700">
                      Completed {milestone.completedAt ? 
                        new Date(milestone.completedAt.toDate()).toLocaleDateString() : 
                        'recently'
                      }
                    </p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};